import { useNavigate } from "react-router-dom";
import { LotteryStatistics } from "../../../../../models/LotteryStatistics";
import { CURRENCY } from "../../../../../utils/Constants";
import { useSpring, animated, easings } from "react-spring";
import { mumboJumbo } from "../../../../../utils/Utils";
import { useInView } from "react-intersection-observer";

export interface OverallStatisticsProps {
  statistics: LotteryStatistics;
}

export default function OverallStatistcis(props: OverallStatisticsProps) {
  const navigate = useNavigate();
  const { ref: refAwarded, inView: inViewAwarded } = useInView({
    triggerOnce: true,
    threshold: 0.2,
  });
  const { ref: refRaised, inView: inViewRaised } = useInView({
    triggerOnce: true,
    threshold: 0.2,
  });
  const { ref: refHighestJackpot, inView: inViewHighestJackpot } = useInView({
    triggerOnce: true,
    threshold: 0.2,
  });
  const { ref: refPlayers, inView: inViewPlayers } = useInView({
    triggerOnce: true,
    threshold: 0.2,
  });
  const { ref: refTickets, inView: inViewTickets } = useInView({
    triggerOnce: true,
    threshold: 0.2,
  });
  const { ref: refWinRatio, inView: inViewWinRatio } = useInView({
    triggerOnce: true,
    threshold: 0.2,
  });

  const awardedAnimation = useSpring({
    from: { number: 0 },
    to: {
      number: inViewAwarded ? Number(props.statistics.totalAwarded.value) : 0,
    },
    config: {
      duration: 2000,
      easing: easings.easeOutQuad,
    },
    reset: !inViewAwarded,
  });
  const raisedAnimation = useSpring({
    from: { number: 0 },
    to: {
      number: inViewRaised ? Number(props.statistics.totalRaised.value) : 0,
    },
    config: {
      duration: 2000,
      easing: easings.easeOutQuad,
    },
    reset: !inViewRaised,
  });
  const highestJackpotAnimation = useSpring({
    from: { number: 0 },
    to: {
      number: inViewHighestJackpot
        ? Number(props.statistics.highestJackpot.value)
        : 0,
    },
    config: {
      duration: 2000,
      easing: easings.easeOutQuad,
    },
    reset: !inViewHighestJackpot,
  });
  const playersAnimation = useSpring({
    from: { number: 0 },
    to: {
      number: inViewPlayers ? Number(props.statistics.playersEntered) : 0,
    },
    config: {
      duration: 2000,
      easing: easings.easeOutQuad,
    },
    reset: !inViewPlayers,
  });
  const ticketsAnimation = useSpring({
    from: { number: 0 },
    to: {
      number: inViewTickets ? Number(props.statistics.ticketsBought) : 0,
    },
    config: {
      duration: 2000,
      easing: easings.easeOutQuad,
    },
    reset: !inViewTickets,
  });
  const winRatioAnimation = useSpring({
    from: { number: 0 },
    to: {
      number: inViewWinRatio ? Number(props.statistics.winRatio) : 0,
    },
    config: {
      duration: 2000,
      easing: easings.easeOutQuad,
    },
    reset: !inViewWinRatio,
  });

  return (
    <div className="flex flex-col space-y-1.5 items-center ~mb-5/6">
      <div className="flex md:flex-row flex-col md:space-y-0 space-y-1.5 md:space-x-12 space-x-0 items-center ~mb-3/4">
        <div className="hover:scale-105 duration-250">
          <div
            ref={refAwarded}
            className={`flex flex-col items-start`}
            data-aos="fade-right"
          >
            <span className="~mb-0.5/1 font-barlowc font-bold ~text-base/lg bg-gradient-to-b from-mango to-crail bg-clip-text text-transparent">
              WE HAVE AWARDED OUR PLAYERS A TOTAL OF
            </span>
            <div className="flex flex-row ~mb-1.5/3">
              <animated.div className="font-archb font-bold ~text-xxl/xxxxl ~leading-7/10 bg-clip-text text-transparent bg-gradient-to-br from-white to-emperor">
                {awardedAnimation.number.to((n) =>
                  mumboJumbo(
                    n,
                    props.statistics.totalAwarded.pretty.length ?? 0,
                  ),
                )}
              </animated.div>
              <p className="font-archb font-bold ~text-xxl/xxxxl ~leading-7/10 bg-clip-text text-transparent bg-gradient-to-b from-mango to-crail">
                &nbsp;{CURRENCY}
              </p>
            </div>
          </div>
        </div>
        {props.statistics.totalRaised.value > 0 && (
          <div className="hover:scale-105 duration-250">
            <div
              ref={refRaised}
              onClick={() => navigate("/charity")}
              className={`flex flex-col items-start cursor-pointer`}
              data-aos="fade-left"
            >
              <span className="~mb-0.5/1 font-barlowc font-bold ~text-base/lg bg-gradient-to-b from-bermuda to-ming bg-clip-text text-transparent">
                WE HAVE RAISED FOR CHARITIES A TOTAL OF
              </span>
              <div className="flex flex-row ~mb-0.5/1">
                <animated.div className="font-archb font-bold ~text-xxl/xxxxl ~leading-7/10 bg-clip-text text-transparent bg-gradient-to-br from-white to-emperor">
                  {raisedAnimation.number.to((n) =>
                    mumboJumbo(
                      n,
                      props.statistics.totalRaised.pretty.length ?? 0,
                    ),
                  )}
                </animated.div>
                <p className="font-archb font-bold ~text-xxl/xxxxl ~leading-7/10 bg-clip-text text-transparent bg-gradient-to-b from-bermuda to-ming">
                  &nbsp;{CURRENCY}
                </p>
              </div>
              <span className="font-poppins text-gray ~text-xs/sm">
                Every ticket you buy is an important
                <br />
                contribution to this number.
              </span>
            </div>
          </div>
        )}
      </div>
      <div className="flex md:flex-row flex-col md:space-y-0 space-y-1.5 md:space-x-12 space-x-0 items-center ~mb-5/6">
        <div className="flex flex-col hover:scale-105 duration-250">
          <div
            ref={refTickets}
            className={`flex flex-col items-start`}
            data-aos="fade-right"
          >
            <div className="flex flex-row items-end">
              <animated.div className="font-archb font-bold ~text-xxl/xxxxl ~leading-7/10 bg-clip-text text-transparent bg-gradient-to-br from-white to-emperor">
                {ticketsAnimation.number.to((n) => Math.round(n))}
              </animated.div>
              <p className="font-archb font-bold ~text-xl/xxxl ~leading-6/9 bg-clip-text text-transparent bg-gradient-to-b from-mango to-crail">
                &nbsp;TICKETS
              </p>
              <span className="font-barlowc font-bold ~text-base/lg ~leading-6/7 text-gray">
                &nbsp;&nbsp;BOUGHT BY
              </span>
            </div>
          </div>
          <div
            ref={refPlayers}
            className={`flex flex-col items-start`}
            data-aos="fade-right"
          >
            <div className="flex flex-row ~mb-1.5/3 items-end">
              <animated.div className="font-archb font-bold ~text-xxl/xxxxl ~leading-7/10 bg-clip-text text-transparent bg-gradient-to-br from-white to-emperor">
                {playersAnimation.number.to((n) => Math.round(n))}
              </animated.div>
              <span className="font-barlowc font-bold ~text-base/lg ~leading-6/7 text-gray">
                &nbsp;&nbsp;BEAUTIFUL
              </span>
              <p className="font-archb font-bold ~text-xl/xxxl ~leading-6/9 bg-clip-text text-transparent bg-gradient-to-b from-mango to-crail">
                &nbsp;PLAYERS
              </p>
            </div>
          </div>
        </div>
        <div className="hover:scale-105 duration-250">
          <div
            ref={refWinRatio}
            className={`flex flex-col items-start`}
            data-aos="fade-right"
          >
            <div className="flex flex-row items-end">
              <animated.div className="font-archb font-bold ~text-xxl/xxxxl ~leading-7/10 bg-clip-text text-transparent bg-gradient-to-br from-white to-emperor">
                {winRatioAnimation.number.to((n) => (n * 100).toFixed(2))}
              </animated.div>
              <p className="font-archb font-bold ~text-xxl/xxxxl ~leading-7/10 bg-clip-text text-transparent bg-gradient-to-b from-mango to-crail">
                %
              </p>
            </div>
            <span className="~mb-0.5/1 font-barlowc font-bold ~text-base/lg text-gray">
              OF TICKETS HAVE BEEN PRIZED
            </span>
          </div>
        </div>
        <div className="hover:scale-105 duration-250">
          <div
            ref={refHighestJackpot}
            className={`flex flex-col items-start`}
            data-aos="fade-right"
          >
            <span className="~mb-0.5/1 font-barlowc font-bold ~text-base/lg bg-gradient-to-b from-mango to-crail bg-clip-text text-transparent">
              RECORD JACKPOT
            </span>
            <div className="flex flex-row ~mb-1.5/3">
              <animated.div className="font-archb font-bold ~text-xxl/xxxxl ~leading-7/10 bg-clip-text text-transparent bg-gradient-to-br from-white to-emperor">
                {highestJackpotAnimation.number.to((n) =>
                  mumboJumbo(
                    n,
                    props.statistics.highestJackpot.pretty.length ?? 0,
                  ),
                )}
              </animated.div>
              <p className="font-archb font-bold ~text-xxl/xxxxl ~leading-7/10 bg-clip-text text-transparent bg-gradient-to-b from-mango to-crail">
                &nbsp;{CURRENCY}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
